import Col from 'antd/es/col'
import Row from 'antd/es/row'
import { IdeyaButton } from '@/common/antd/ideya-button/ideya-button.tsx'
import { FormInstance } from 'antd/lib/index'
import { useTranslation } from 'react-i18next'
import { CreateIdeaFormFieldTypes } from './action-idea-form.tsx'
import {
  FillStrategies,
  HorizonOptions,
  ConvictionOptions,
  getTradeReasonsTreeData,
  IDEA_MUTATION_KEYS,
} from './action-idea-form.ts'
import {
  useCreateIdeaMutation,
  useUpdateIdeaMutation,
} from '@/apis/idea/idea-api.api.ts'
import IdeyaTypography from '@/common/antd/ideya-typography.tsx'
import { ArrowRightOutlined, ExclamationCircleFilled } from '@ant-design/icons'
import React from 'react'
import { currencyValueFormatter } from '@/utils/formatters.ts'
import { ValueFormatterParams } from 'ag-grid-enterprise'
import { IdeaDirectionType } from '@/enums/idea-list.enum.ts'
import { IdeaDetail } from '@/apis/idea/idea-api.def.ts'
import { isEqual } from 'lodash'
import './action-idea-confirm-form.scss'
import Tag from 'antd/es/tag'

const getDecimalPlaces = (value: number | undefined) => {
  if (value == null || !Number.isFinite(value) || Number.isInteger(value)) {
    return 0
  }
  const str = value.toString()
  if (str.includes('e-')) {
    return parseInt(str.split('e-')[1], 10)
  }
  return str.split('.')[1]?.length ?? 0
}

const formatCurrency = (value: number | undefined, currency = 'USD') =>
  currencyValueFormatter({ value } as ValueFormatterParams, {
    decimal: getDecimalPlaces(value),
    currency,
  }) ?? '-'

interface IdeaConfirmProps {
  form: FormInstance<CreateIdeaFormFieldTypes>
  tickerLabel: string
  onCancel: () => void
  onSubmit: () => void
  ideaDetail?: IdeaDetail
  tickerCurrency?: string
  hasTargetMarketValueEdited: boolean
}

interface IdeaInfoProps {
  label: string
  value: string | React.ReactNode
}

const ValueWithChange = ({
  newDisplay,
  oldDisplay,
  hasChanged,
}: {
  newDisplay: React.ReactNode
  oldDisplay: string
  hasChanged: boolean
}) => {
  if (!hasChanged) {
    return (
      <IdeyaTypography.Text
        size={'lg'}
        textStyle={'strong'}
        className={
          'action-idea-confirm-form__value action-idea-confirm-form__value--no-change'
        }
      >
        {newDisplay}
      </IdeyaTypography.Text>
    )
  }
  return (
    <Row
      justify={'end'}
      align={'middle'}
      className={'action-idea-confirm-form__value-with-change'}
    >
      <IdeyaTypography.Text
        size={'lg'}
        className={'action-idea-confirm-form__old-value'}
      >
        {oldDisplay}
      </IdeyaTypography.Text>
      <ArrowRightOutlined className={'action-idea-confirm-form__arrow-icon'} />
      <IdeyaTypography.Text
        size={'lg'}
        textStyle={'strong'}
        className={
          'action-idea-confirm-form__value action-idea-confirm-form__value--changed'
        }
      >
        {newDisplay}
      </IdeyaTypography.Text>
    </Row>
  )
}

const RowInfo = ({
  titleComponent,
  infos = [],
}: {
  titleComponent: React.ReactNode
  infos?: IdeaInfoProps[]
}) => (
  <Row className={'action-idea-confirm-form__row-info'}>
    {titleComponent}
    <Row className={'action-idea-confirm-form__row-info-content'}>
      {infos.map((info, index) => (
        <Row
          justify={'space-between'}
          className={'action-idea-confirm-form__row-info-item'}
          key={index}
        >
          <IdeyaTypography.Text
            size={'base'}
            textStyle={'normal'}
            className={'action-idea-confirm-form__label'}
          >
            {info.label}:
          </IdeyaTypography.Text>
          {React.isValidElement(info.value) ? (
            info.value
          ) : (
            <IdeyaTypography.Text
              size={'lg'}
              textStyle={'strong'}
              className={'action-idea-confirm-form__value'}
            >
              {info.value}
            </IdeyaTypography.Text>
          )}
        </Row>
      ))}
    </Row>
  </Row>
)

const RowInfoTitle = ({
  text,
  icon,
  tag,
}: {
  text: string
  icon?: React.ReactNode
  tag?: React.ReactNode
}) => (
  <Row
    justify={'space-between'}
    align={'middle'}
    className={'action-idea-confirm-form__title'}
  >
    <div className={'action-idea-confirm-form__title-label'}>
      {icon}
      <IdeyaTypography.Text
        size={'lg'}
        textStyle={'strong'}
        className={'action-idea-confirm-form__title-text'}
      >
        {text}
      </IdeyaTypography.Text>
    </div>
    {tag}
  </Row>
)

export const ActionIdeaConfirmForm: React.FC<IdeaConfirmProps> = ({
  form,
  tickerLabel,
  onCancel,
  onSubmit,
  ideaDetail,
  tickerCurrency,
  hasTargetMarketValueEdited,
}) => {
  const { t } = useTranslation()

  const priceCurrency = tickerCurrency ?? ideaDetail?.tickerCurrency ?? 'USD'

  const [, { isLoading: isCreating, isSuccess: isCreateSuccess }] =
    useCreateIdeaMutation({
      fixedCacheKey: IDEA_MUTATION_KEYS.CREATE,
    })
  const [, { isLoading: isUpdating, isSuccess: isUpdateSuccess }] =
    useUpdateIdeaMutation({
      fixedCacheKey: IDEA_MUTATION_KEYS.UPDATE,
    })
  const isSubmitting = isCreating || isUpdating
  const isDone = isCreateSuccess || isUpdateSuccess

  const isEdit = !!ideaDetail

  const tradeReasonsTreeData = React.useMemo(
    () => getTradeReasonsTreeData(t),
    [t]
  )

  const translateFillStrategy = (value: string | undefined) => {
    const found = FillStrategies.find((fs) => fs.value === value)
    return found ? t(found.labelKey) : (value ?? '-')
  }

  const translateHorizon = (value: string | undefined) => {
    const found = HorizonOptions.find((h) => h.value === value)
    return found ? t(found.labelKey) : (value ?? '-')
  }

  const translateConviction = (value: string | undefined) => {
    const found = ConvictionOptions.find((c) => c.value === value)
    return found ? t(found.labelKey) : (value ?? '-')
  }

  const translateTradeReason = (value: string) => {
    for (const category of tradeReasonsTreeData) {
      if (category.value === value) return category.title
      for (const child of category.children ?? []) {
        if (child.value === value) return child.title
      }
    }
    return value
  }

  const initialFillStrategy =
    ideaDetail?.fillStrategy === '15min-VWAP' ||
    ideaDetail?.fillStrategy === '1hour-VWAP'
      ? FillStrategies[0].value
      : ideaDetail?.fillStrategy

  const hasFillStrategyChanged =
    isEdit && form.getFieldValue('fillStrategy') !== initialFillStrategy

  // Any field in the "Order Details" card creates a new order when changed.
  const hasTargetChanges =
    (isEdit && hasTargetMarketValueEdited) || hasFillStrategyChanged

  const target: IdeaInfoProps[] = [
    {
      label: t('idea_list.idea_modal.action_idea_form.ticker'),
      value: (
        <IdeyaTypography.Text
          size={'lg'}
          textStyle={'strong'}
          className={'action-idea-confirm-form__ticker-value'}
        >
          {tickerLabel}
        </IdeyaTypography.Text>
      ),
    },
    {
      label: t('idea_list.idea_modal.action_idea_form.direction'),
      value: (
        <IdeyaTypography.Text
          size={'lg'}
          textStyle={'strong'}
          className={
            form.getFieldValue('direction') === IdeaDirectionType.Long
              ? 'action-idea-confirm-form__direction-long'
              : 'action-idea-confirm-form__direction-short'
          }
        >
          {!form.getFieldValue('direction') && '-'}
          {form.getFieldValue('direction') === IdeaDirectionType.Long &&
            t('idea_list.idea_table.direction.long')}
          {form.getFieldValue('direction') === IdeaDirectionType.Short &&
            t('idea_list.idea_table.direction.short')}
        </IdeyaTypography.Text>
      ),
    },
    {
      label: t('idea_list.idea_modal.confirm_idea_form.target_mv'),
      value: (
        <Row
          justify={'end'}
          align={'middle'}
          className={'action-idea-confirm-form__value-with-change'}
        >
          <Tag
            color={'orange'}
            bordered
            className={'action-idea-confirm-form__current-mv-tag'}
          >
            {`${t('idea_list.idea_modal.confirm_idea_form.current_mv')}: ${formatCurrency(Math.round(ideaDetail?.currentMarketValue ?? 0))}`}
          </Tag>
          <IdeyaTypography.Text
            size={'lg'}
            textStyle={'strong'}
            className={
              isEdit && hasTargetMarketValueEdited
                ? 'action-idea-confirm-form__value action-idea-confirm-form__value--changed'
                : 'action-idea-confirm-form__value'
            }
          >
            {formatCurrency(form.getFieldValue('targetMarketValue'))}
          </IdeyaTypography.Text>
        </Row>
      ),
    },
    {
      label: t('idea_list.idea_modal.action_idea_form.fill_strategy'),
      value: (
        <ValueWithChange
          newDisplay={translateFillStrategy(form.getFieldValue('fillStrategy'))}
          oldDisplay={translateFillStrategy(initialFillStrategy)}
          hasChanged={
            isEdit && form.getFieldValue('fillStrategy') !== initialFillStrategy
          }
        />
      ),
    },
  ]
  const strategy: IdeaInfoProps[] = [
    {
      label: t('idea_list.idea_modal.action_idea_form.auto_fill'),
      value: (
        <ValueWithChange
          newDisplay={
            form.getFieldValue('auto_fill')
              ? t('common.true')
              : t('common.false')
          }
          oldDisplay={
            ideaDetail?.auto_fill ? t('common.true') : t('common.false')
          }
          hasChanged={
            isEdit && form.getFieldValue('auto_fill') !== ideaDetail?.auto_fill
          }
        />
      ),
    },
    {
      label: t('idea_list.idea_modal.confirm_idea_form.target_px'),
      value: (
        <ValueWithChange
          newDisplay={formatCurrency(
            form.getFieldValue('target'),
            priceCurrency
          )}
          oldDisplay={formatCurrency(ideaDetail?.target, priceCurrency)}
          hasChanged={
            isEdit && form.getFieldValue('target') !== ideaDetail?.target
          }
        />
      ),
    },
    {
      label: t('idea_list.idea_modal.confirm_idea_form.stop_px'),
      value: (
        <ValueWithChange
          newDisplay={formatCurrency(form.getFieldValue('stop'), priceCurrency)}
          oldDisplay={formatCurrency(ideaDetail?.stop, priceCurrency)}
          hasChanged={isEdit && form.getFieldValue('stop') !== ideaDetail?.stop}
        />
      ),
    },
    {
      label: t('idea_list.idea_modal.action_idea_form.trade_reason'),
      value: (() => {
        const rawNew = form.getFieldValue('tradeReason')
        const newReasons = (
          Array.isArray(rawNew)
            ? rawNew
            : (rawNew ?? '')
                .split(',')
                .map((r: string) => r.trim())
                .filter(Boolean)
        )
          .slice()
          .sort()
        const oldReasons = (ideaDetail?.tradeReason ?? '')
          .split(',')
          .map((r) => r.trim())
          .filter(Boolean)
          .slice()
          .sort()
        const newVal = newReasons.map(translateTradeReason).join(', ') || '-'
        const oldVal = oldReasons.map(translateTradeReason).join(', ') || '-'
        return (
          <ValueWithChange
            newDisplay={newVal}
            oldDisplay={oldVal}
            hasChanged={isEdit && !isEqual(newReasons, oldReasons)}
          />
        )
      })(),
    },
    {
      label: t('idea_list.idea_modal.action_idea_form.horizon'),
      value: (
        <ValueWithChange
          newDisplay={translateHorizon(form.getFieldValue('horizon'))}
          oldDisplay={translateHorizon(ideaDetail?.horizon)}
          hasChanged={
            isEdit && form.getFieldValue('horizon') !== ideaDetail?.horizon
          }
        />
      ),
    },
    {
      label: t('idea_list.idea_modal.action_idea_form.conviction'),
      value: (
        <ValueWithChange
          newDisplay={translateConviction(form.getFieldValue('conviction'))}
          oldDisplay={translateConviction(ideaDetail?.conviction)}
          hasChanged={
            isEdit &&
            form.getFieldValue('conviction') !== ideaDetail?.conviction
          }
        />
      ),
    },
  ]
  return (
    <>
      <div className={'action-idea-confirm-form__content'}>
        <div className={'action-idea-confirm-form__body'}>
          <IdeyaTypography.Text>
            {t('idea_list.idea_modal.confirm_idea_form.review_details')}
          </IdeyaTypography.Text>
          <div className={'action-idea-confirm-form__sections'}>
            <RowInfo
              titleComponent={
                <RowInfoTitle
                  text={t(
                    'idea_list.idea_modal.confirm_idea_form.create_new_order_title'
                  )}
                  tag={
                    isEdit && hasTargetChanges ? (
                      <Tag
                        className={
                          'action-idea-confirm-form__title-tag action-idea-confirm-form__title-tag--new-order'
                        }
                      >
                        {t(
                          'idea_list.idea_modal.confirm_idea_form.create_new_order'
                        )}
                      </Tag>
                    ) : undefined
                  }
                />
              }
              infos={target}
            />
            <RowInfo
              titleComponent={
                <RowInfoTitle
                  text={t(
                    'idea_list.idea_modal.confirm_idea_form.update_idea_only_title'
                  )}
                  tag={
                    isEdit ? (
                      <Tag
                        className={
                          'action-idea-confirm-form__title-tag action-idea-confirm-form__title-tag--no-impact'
                        }
                      >
                        {t(
                          'idea_list.idea_modal.confirm_idea_form.no_order_impact'
                        )}
                      </Tag>
                    ) : undefined
                  }
                />
              }
              infos={strategy}
            />
          </div>
        </div>
      </div>
      <div className={'action-idea-confirm-form__footer'}>
        <Row
          justify={'space-between'}
          align={'middle'}
          className={'action-idea-confirm-form__footer-row'}
        >
          {isEdit && (
            <Row className={'action-idea-confirm-form__warning'} align={'top'}>
              <ExclamationCircleFilled
                className={'action-idea-confirm-form__warning-icon'}
              />
              <IdeyaTypography.Text size={'base'} textStyle={'strong'}>
                {hasTargetChanges
                  ? t(
                      'idea_list.idea_modal.confirm_idea_form.warning_new_order'
                    )
                  : t(
                      'idea_list.idea_modal.confirm_idea_form.warning_update_only'
                    )}
              </IdeyaTypography.Text>
            </Row>
          )}
          <Row
            gutter={[8, 8]}
            className={'action-idea-confirm-form__button-group'}
            justify={'end'}
          >
            <Col>
              <IdeyaButton ghost onClick={onCancel}>
                {t('idea_list.idea_modal.confirm_idea_form.back_to_edit')}
              </IdeyaButton>
            </Col>
            <Col>
              <IdeyaButton
                onClick={onSubmit}
                loading={isSubmitting}
                disabled={isSubmitting || isDone}
              >
                {!isEdit
                  ? t('idea_list.idea_modal.confirm_idea_form.confirm_submit')
                  : hasTargetChanges
                    ? t(
                        'idea_list.idea_modal.confirm_idea_form.create_new_order'
                      )
                    : t(
                        'idea_list.idea_modal.confirm_idea_form.update_current_idea'
                      )}
              </IdeyaButton>
            </Col>
          </Row>
        </Row>
      </div>
    </>
  )
}
